import { ArrowUpRight, Download, MonitorDown } from "lucide-react";
import type { ReactNode } from "react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { desktopLink } from "./bridge";
import { DESKTOP_FEATURES, hasFeature, type DesktopFeature } from "./edition";
import { DOWNLOAD_URL } from "@/lib/landing";

/** "Abrir no app": abre o mesmo caminho no Kompil Desktop instalado (link kompil://). */
export function OpenInDesktop({
  path,
  className,
  children,
}: {
  path?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <a href={desktopLink(path)} className={cn(buttonVariants({ variant: "secondary" }), className)}>
      <ArrowUpRight className="h-3.5 w-3.5" /> {children ?? "Abrir no app"}
    </a>
  );
}

export function DownloadDesktop({ className, size }: { className?: string; size?: "lg" }) {
  return (
    <a href={DOWNLOAD_URL} className={cn(buttonVariants({ variant: "primary", size }), "glow", className)}>
      <Download className="h-3.5 w-3.5" /> Baixar o Kompil Desktop
    </a>
  );
}

/**
 * Recurso do produto completo (app desktop). Na web resumida, no lugar da página aparece o
 * convite para o app — com o link que abre exatamente esta tela no Kompil Desktop.
 */
export function DesktopOnly({ feature, children }: { feature: DesktopFeature; children: ReactNode }) {
  if (hasFeature(feature)) return <>{children}</>;
  const info = DESKTOP_FEATURES[feature];
  const others = (Object.keys(DESKTOP_FEATURES) as DesktopFeature[]).filter(
    (f) => f !== feature && f !== "admin",
  );
  return (
    <div className="mx-auto max-w-3xl animate-fade-up py-6">
      <div className="surface relative overflow-hidden rounded-2xl border border-border p-8">
        <div aria-hidden className="bg-halo pointer-events-none absolute inset-0 opacity-60" />
        <div className="relative">
          <span className="surface inline-flex h-12 w-12 items-center justify-center rounded-xl border border-border-strong">
            <info.icon className="h-5 w-5" />
          </span>
          <p className="label-mono mt-5">Kompil Desktop</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{info.title} está no app desktop</h1>
          <p className="mt-2 max-w-xl text-[14px] text-fg-muted">{info.description}</p>
          <p className="mt-3 max-w-xl text-[13px] text-fg-subtle">
            Na web você edita código e conversa de qualquer lugar. O produto completo — terminal, IA,
            histórico, rede de devs, plugins e temas — roda no Kompil Desktop.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <DownloadDesktop />
            <OpenInDesktop />
          </div>
        </div>
      </div>
      <p className="label-mono mb-3 mt-8">Também no app desktop</p>
      <ul className="grid gap-3 sm:grid-cols-2">
        {others.map((f) => {
          const o = DESKTOP_FEATURES[f];
          return (
            <li key={f} className="surface flex gap-3 rounded-xl border border-border p-4">
              <o.icon className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" />
              <div>
                <p className="text-[13.5px] font-medium">{o.title}</p>
                <p className="mt-0.5 text-[12.5px] text-fg-subtle">{o.description}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Botão compacto para barras (ex.: "Executar" na web abre o projeto no app). */
export function DesktopBadgeButton({ path, label }: { path?: string; label: string }) {
  return (
    <a
      href={desktopLink(path)}
      className={cn(buttonVariants({ variant: "primary" }), "glow")}
      title="Abre este projeto no Kompil Desktop (terminal, execução e IA ficam no app)"
    >
      <MonitorDown className="h-3.5 w-3.5" /> {label}
    </a>
  );
}
